export type TimezoneOption = {
  label: string;
  value: string;
  offset: string;
  group?: string;
};

const DEFAULT_TIMEZONE = "UTC";
const STORAGE_KEY = "app_timezone";
export const TIMEZONE_CHANGED_EVENT = "app_timezone_changed";

type FallbackEntry = { tz: string; baseOffset: string; dstOffset?: string };

const FALLBACK_ENTRIES: FallbackEntry[] = [
  { tz: "UTC", baseOffset: "UTC+00:00" },
  { tz: "Europe/London", baseOffset: "UTC+00:00", dstOffset: "UTC+01:00" },
  { tz: "Europe/Paris", baseOffset: "UTC+01:00", dstOffset: "UTC+02:00" },
  { tz: "Europe/Berlin", baseOffset: "UTC+01:00", dstOffset: "UTC+02:00" },
  { tz: "Europe/Madrid", baseOffset: "UTC+01:00", dstOffset: "UTC+02:00" },
  { tz: "Europe/Rome", baseOffset: "UTC+01:00", dstOffset: "UTC+02:00" },
  { tz: "Europe/Amsterdam", baseOffset: "UTC+01:00", dstOffset: "UTC+02:00" },
  { tz: "Europe/Brussels", baseOffset: "UTC+01:00", dstOffset: "UTC+02:00" },
  { tz: "Europe/Vienna", baseOffset: "UTC+01:00", dstOffset: "UTC+02:00" },
  { tz: "Europe/Stockholm", baseOffset: "UTC+01:00", dstOffset: "UTC+02:00" },
  { tz: "America/New_York", baseOffset: "UTC-05:00", dstOffset: "UTC-04:00" },
  { tz: "America/Chicago", baseOffset: "UTC-06:00", dstOffset: "UTC-05:00" },
  { tz: "America/Denver", baseOffset: "UTC-07:00", dstOffset: "UTC-06:00" },
  { tz: "America/Los_Angeles", baseOffset: "UTC-08:00", dstOffset: "UTC-07:00" },
  { tz: "America/Toronto", baseOffset: "UTC-05:00", dstOffset: "UTC-04:00" },
  { tz: "America/Mexico_City", baseOffset: "UTC-06:00" },
  { tz: "America/Sao_Paulo", baseOffset: "UTC-03:00" },
  { tz: "America/Buenos_Aires", baseOffset: "UTC-03:00" },
  { tz: "Asia/Kolkata", baseOffset: "UTC+05:30" },
  { tz: "Asia/Dubai", baseOffset: "UTC+04:00" },
  { tz: "Asia/Riyadh", baseOffset: "UTC+03:00" },
  { tz: "Asia/Singapore", baseOffset: "UTC+08:00" },
  { tz: "Asia/Tokyo", baseOffset: "UTC+09:00" },
  { tz: "Asia/Shanghai", baseOffset: "UTC+08:00" },
  { tz: "Asia/Hong_Kong", baseOffset: "UTC+08:00" },
  { tz: "Asia/Seoul", baseOffset: "UTC+09:00" },
  { tz: "Asia/Bangkok", baseOffset: "UTC+07:00" },
  { tz: "Asia/Jakarta", baseOffset: "UTC+07:00" },
  { tz: "Australia/Sydney", baseOffset: "UTC+10:00", dstOffset: "UTC+11:00" },
  { tz: "Australia/Melbourne", baseOffset: "UTC+10:00", dstOffset: "UTC+11:00" },
  { tz: "Pacific/Auckland", baseOffset: "UTC+12:00", dstOffset: "UTC+13:00" },
  { tz: "Africa/Cairo", baseOffset: "UTC+02:00" },
  { tz: "Africa/Johannesburg", baseOffset: "UTC+02:00" },
  { tz: "Africa/Lagos", baseOffset: "UTC+01:00" },
  { tz: "Africa/Nairobi", baseOffset: "UTC+03:00" },
];

const FALLBACK_TIMEZONES = FALLBACK_ENTRIES.map((e) => e.tz);

function isCurrentlyDst(date: Date, timeZone: string): boolean {
  try {
    const jan = new Date(date.getFullYear(), 0, 1);
    const jul = new Date(date.getFullYear(), 6, 1);
    const fmtJan = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "shortOffset" });
    const fmtJul = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "shortOffset" });
    const getOffset = (fmt: Intl.DateTimeFormat, d: Date) => {
      const part = fmt.formatToParts(d).find((p) => p.type === "timeZoneName")?.value ?? "";
      return part;
    };
    const janOff = getOffset(fmtJan, jan);
    const julOff = getOffset(fmtJul, jul);
    const currentOff = getOffset(
      new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "shortOffset" }),
      date,
    );
    if (janOff === julOff) return false;
    return currentOff !== janOff;
  } catch {
    return false;
  }
}

function getHardcodedOffset(tz: string, now: Date = new Date()): string | null {
  const entry = FALLBACK_ENTRIES.find((e) => e.tz === tz);
  if (!entry) return null;
  if (entry.dstOffset && isCurrentlyDst(now, tz)) {
    return entry.dstOffset;
  }
  return entry.baseOffset;
}

/** Get stored or system timezone */
export function getTimezone(): string {
  if (typeof window === "undefined") return DEFAULT_TIMEZONE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && stored.trim()) return stored.trim();
    const system = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return system || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/** Set active timezone and broadcast change event */
export function setTimezone(timezone: string): void {
  if (typeof window === "undefined") return;
  try {
    const trimmed = timezone.trim();
    if (!trimmed) return;
    window.localStorage.setItem(STORAGE_KEY, trimmed);
    window.dispatchEvent(
      new CustomEvent(TIMEZONE_CHANGED_EVENT, { detail: { timezone: trimmed } }),
    );
  } catch (err) {
    console.error("Failed to save timezone preference:", err);
  }
}

function parseOffsetFromMinutes(totalMinutes: number): string {
  const sign = totalMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(totalMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return `UTC${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** Calculate formatted offset string for a timezone at present, e.g. "UTC+05:30" */
export function getTimezoneOffsetString(timeZone: string): string {
  const now = new Date();

  const hardcoded = getHardcodedOffset(timeZone, now);
  if (hardcoded) return hardcoded;

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(now);
    const tzPart = parts.find((p) => p.type === "timeZoneName")?.value;
    if (tzPart) {
      if (tzPart === "GMT" || tzPart === "UTC") return "UTC+00:00";
      const match = tzPart.match(/(GMT|UTC)?([+-])(\d{1,2})(?::(\d{2}))?/);
      if (match) {
        const sign = match[2];
        const hours = match[3].padStart(2, "0");
        const minutes = match[4] || "00";
        return `UTC${sign}${hours}:${minutes}`;
      }
    }
  } catch {
    // fall through to alternative methods
  }

  try {
    const utcDate = new Date(now.toLocaleString("en-US", { timeZone: "UTC" }));
    const tzDate = new Date(now.toLocaleString("en-US", { timeZone }));
    const diffMs = tzDate.getTime() - utcDate.getTime();
    if (!Number.isNaN(diffMs)) {
      const diffMinutes = Math.round(diffMs / 60000);
      return parseOffsetFromMinutes(diffMinutes);
    }
  } catch {
    // fall through
  }

  return "UTC+00:00";
}

/** Utility to generate full options list for timezone dropdown */
export function formatTimezoneOption(tz: string): TimezoneOption {
  const offset = getTimezoneOffsetString(tz);
  const cleanName = tz.replace(/_/g, " ");
  return {
    value: tz,
    label: `(${offset}) ${cleanName}`,
    offset,
  };
}

/** Build a timezone option deterministically from hardcoded data — NO Intl calls, 100% synchronous.
 *  Uses standard (non-DST) offset intentionally; fetchOrGetTimezones will correct it after hydration. */
function buildHardcodedOption(entry: FallbackEntry): TimezoneOption {
  const offset = entry.baseOffset;
  const cleanName = entry.tz.replace(/_/g, " ");
  return {
    value: entry.tz,
    label: `(${offset}) ${cleanName}`,
    offset,
  };
}

/** Get immediate synchronous timezone options (for initial render) — avoids Intl for SSR consistency */
export function getImmediateTimezones(): TimezoneOption[] {
  try {
    const options = FALLBACK_ENTRIES.map((entry) => buildHardcodedOption(entry));
    options.sort((a, b) => {
      if (a.offset !== b.offset) {
        return a.offset.localeCompare(b.offset);
      }
      return a.value.localeCompare(b.value);
    });
    if (options.length === 0) {
      throw new Error("Hardcoded list empty");
    }
    return options;
  } catch {
    const utcOpt: TimezoneOption = {
      value: "UTC",
      label: "(UTC+00:00) UTC",
      offset: "UTC+00:00",
    };
    return [utcOpt];
  }
}

/** Fetch timezones from external API with instant fallback */
export async function fetchOrGetTimezones(): Promise<TimezoneOption[]> {
  let rawList: string[] = [];

  // Use JS native supported values first (most reliable and fast)
  if (typeof Intl !== "undefined" && "supportedValuesOf" in Intl) {
    try {
      const supportedValuesOf = Intl.supportedValuesOf as ((input: string) => string[]) | undefined;
      if (supportedValuesOf) {
        const raw = supportedValuesOf("timeZone");
        if (Array.isArray(raw) && raw.length > 0) {
          rawList = raw.filter((tz) => typeof tz === "string" && tz.trim().length > 0);
          console.log(`[Timezone] Loaded ${rawList.length} timezones from Intl.supportedValuesOf`);
        }
      }
    } catch (err) {
      console.log("[Timezone] Intl.supportedValuesOf failed:", err);
      rawList = [];
    }
  }

  // Fallback to hardcoded list if environment is restricted
  if (!Array.isArray(rawList) || rawList.length === 0) {
    console.log("[Timezone] Using hardcoded fallback list");
    rawList = FALLBACK_TIMEZONES.slice();
  }

  let options: TimezoneOption[] = [];
  try {
    options = rawList.map((tz) => formatTimezoneOption(tz));
  } catch (err) {
    console.error("[Timezone] formatTimezoneOption map failed:", err);
    options = [];
  }

  if (!Array.isArray(options) || options.length === 0) {
    console.error("[Timezone] Options empty after map, rebuilding from hardcoded list");
    options = FALLBACK_ENTRIES.map((entry) => {
      const offset = getHardcodedOffset(entry.tz) || entry.baseOffset;
      const cleanName = entry.tz.replace(/_/g, " ");
      return {
        value: entry.tz,
        label: `(${offset}) ${cleanName}`,
        offset,
      };
    });
  }

  // Sort by offset then name
  options.sort((a, b) => {
    try {
      if (a.offset !== b.offset) {
        return a.offset.localeCompare(b.offset);
      }
      return a.value.localeCompare(b.value);
    } catch {
      return 0;
    }
  });

  console.log(`[Timezone] Returning ${options.length} formatted timezone options`);
  return options;
}
